import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Shield, Upload, FileText, Download, ChevronRight, ChevronDown,
  AlertTriangle, Check, X, Loader2, ExternalLink, Search, Filter,
  ShieldCheck, ShieldAlert, Bug, Server
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Severity colors
const SEVERITY_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#2563eb',
  info: '#6b7280',
};

// Report type labels
const REPORT_TYPE_LABELS = {
  web_application_audit: 'Web App Audit',
  vulnerability_assessment: 'VA Scan',
  malware_monitoring: 'Malware Check',
  unknown: 'Other',
};

// Badge component
function Badge({ children, color = '#6b7280', size = 'normal' }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: size === 'small' ? '2px 6px' : '3px 10px',
        borderRadius: 999,
        fontSize: size === 'small' ? 10 : 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        background: color + '18',
        color,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  );
}

// Card component
function Card({ children, className = '', onClick, style = {} }) {
  return (
    <div
      className={`eyvo-card ${className} ${onClick ? 'clickable' : ''}`}
      onClick={onClick}
      style={style}
    >
      {children}
    </div>
  );
}

// Severity Pills component
function SeverityPills({ counts }) {
  return (
    <div className="severity-pills">
      {['critical', 'high', 'medium', 'low', 'info'].map((sev) => (
        <span
          key={sev}
          className="severity-pill"
          style={{ color: SEVERITY_COLORS[sev] }}
          title={`${counts[sev] || 0} ${sev}`}
        >
          {counts[sev] || 0}
          {sev[0].toUpperCase()}
        </span>
      ))}
    </div>
  );
}

// Upload Section
function UploadSection({ onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleUpload = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('report_label', file.name);

    try {
      const response = await fetch(`${API_URL}/api/pentest/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }

      toast.success(`Report parsed: ${data.total_findings} findings extracted`);
      onUploadComplete?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  return (
    <Card className="upload-section">
      <h3><Upload size={18} /> Upload Pen Test Report</h3>
      <div
        className={`upload-dropzone ${dragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="pentest-upload-dropzone"
      >
        {uploading ? (
          <>
            <Loader2 className="spin" size={32} />
            <p>Parsing report...</p>
          </>
        ) : (
          <>
            <FileText size={32} />
            <p>Drop PDF file here or click to browse</p>
            <span>Supports Indusface Web Audit, VA, and Malware reports</span>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
              data-testid="pentest-file-input"
            />
          </>
        )}
      </div>
    </Card>
  );
}

// Report Card with expandable findings
function ReportCard({ report, onAccept, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [findings, setFindings] = useState([]);
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const loadFindings = async () => {
    if (findings.length > 0) return;
    setLoadingFindings(true);
    try {
      const response = await fetch(`${API_URL}/api/pentest/reports/${report.report_id}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setFindings(data.findings || []);
      }
    } catch (error) {
      console.error('Failed to load findings:', error);
    } finally {
      setLoadingFindings(false);
    }
  };

  const handleExpand = () => {
    if (!expanded) loadFindings();
    setExpanded(!expanded);
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const response = await fetch(`${API_URL}/api/pentest/reports/${report.report_id}/accept`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Accept failed');
      }

      toast.success(`${data.findings_created?.length || 0} findings added to tracker`);
      onRefresh?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAccepting(false);
    }
  };

  const handleDownload = () => {
    // Downloads use query token from cookie (logged by server)
    const cookies = document.cookie.split(';').reduce((acc, c) => {
      const [key, val] = c.trim().split('=');
      acc[key] = val;
      return acc;
    }, {});
    const token = cookies['session_token'] || '';
    window.open(`${API_URL}/api/pentest/reports/${report.report_id}/download?token=${token}`, '_blank');
  };

  return (
    <Card className="report-card-pentest">
      <div className="report-header" onClick={handleExpand}>
        <div className="report-info">
          <div className="report-title">
            <Badge color={SEVERITY_COLORS.low}>
              {REPORT_TYPE_LABELS[report.report_type] || 'Report'}
            </Badge>
            <h4>{report.label || report.filename}</h4>
          </div>
          <div className="report-meta">
            <span>{report.scanner || 'Unknown Scanner'}</span>
            <span>•</span>
            <span>{report.scan_date || 'Unknown date'}</span>
            <span>•</span>
            <span>{report.target || 'Unknown target'}</span>
          </div>
        </div>
        <div className="report-summary">
          <SeverityPills counts={report.severity_counts || {}} />
          <span className="expand-icon">
            {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="report-details">
          <div className="report-actions">
            {!report.findings_accepted ? (
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="btn-primary"
                data-testid={`accept-${report.report_id}`}
              >
                {accepting ? <Loader2 className="spin" size={16} /> : <Check size={16} />}
                Accept into Tracker
              </button>
            ) : (
              <Badge color="#059669">
                <Check size={12} /> Accepted
              </Badge>
            )}
            <button onClick={handleDownload} className="btn-secondary" data-testid={`download-${report.report_id}`}>
              <Download size={16} /> Download PDF
            </button>
          </div>

          {loadingFindings ? (
            <div className="loading-findings">
              <Loader2 className="spin" size={20} />
              <span>Loading findings...</span>
            </div>
          ) : findings.length > 0 ? (
            <div className="findings-list">
              <h5>Findings ({findings.length})</h5>
              {findings.map((finding, i) => (
                <div key={i} className="finding-item">
                  <Badge color={SEVERITY_COLORS[finding.severity] || '#6b7280'} size="small">
                    {finding.severity?.toUpperCase()}
                  </Badge>
                  <div className="finding-details">
                    <span className="finding-title">{finding.title}</span>
                    {finding.cvss_score > 0 && (
                      <span className="finding-cvss">CVSS: {finding.cvss_score}</span>
                    )}
                    <div className="finding-controls">
                      {finding.control_ids?.map((cid) => (
                        <Link key={cid} to={`/controls?id=${cid}`} className="control-chip">
                          {cid}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-findings">No findings parsed from this report.</p>
          )}
        </div>
      )}
    </Card>
  );
}

// Main Pentest Page
function PentestPage() {
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [reportsRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/pentest/reports`, { credentials: 'include' }),
        fetch(`${API_URL}/api/pentest/summary`, { credentials: 'include' }),
      ]);

      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setReports(data.reports || []);
      }

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch pentest data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="pentest-page loading">
        <Loader2 className="spin" size={32} />
        <p>Loading security scans...</p>
      </div>
    );
  }

  return (
    <div className="pentest-page" data-testid="pentest-page">
      <div className="page-header">
        <div>
          <h1><Shield size={24} /> Security Scans</h1>
          <p>Upload and manage penetration test reports</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && summary.total_reports > 0 && (
        <div className="pentest-summary">
          <Card className="summary-card">
            <div className="summary-icon">
              <ShieldCheck size={24} color="#059669" />
            </div>
            <div className="summary-info">
              <span className="summary-value">{summary.total_reports}</span>
              <span className="summary-label">Total Reports</span>
            </div>
          </Card>
          <Card className="summary-card">
            <div className="summary-icon">
              <Bug size={24} color="#d97706" />
            </div>
            <div className="summary-info">
              <SeverityPills counts={summary.aggregate_severity || {}} />
              <span className="summary-label">Aggregate Findings</span>
            </div>
          </Card>
          <Card className="summary-card">
            <div className="summary-icon">
              {summary.malware_status === 'clean' ? (
                <ShieldCheck size={24} color="#059669" />
              ) : (
                <ShieldAlert size={24} color="#d97706" />
              )}
            </div>
            <div className="summary-info">
              <span className="summary-value" style={{ color: summary.malware_status === 'clean' ? '#059669' : '#d97706' }}>
                {summary.malware_status === 'clean' ? 'Clean' : summary.malware_status === 'no_data' ? 'No Data' : 'Alerts'}
              </span>
              <span className="summary-label">Malware Status</span>
            </div>
          </Card>
        </div>
      )}

      {/* Upload Section */}
      <UploadSection onUploadComplete={fetchData} />

      {/* Reports List */}
      <h2 className="section-title">Uploaded Reports</h2>
      {reports.length === 0 ? (
        <Card className="empty-state">
          <FileText size={48} color="#6b7280" />
          <h3>No reports uploaded yet</h3>
          <p>Upload your first pen test report above to get started.</p>
        </Card>
      ) : (
        <div className="reports-list-pentest">
          {reports.map((report) => (
            <ReportCard
              key={report.report_id}
              report={report}
              onRefresh={fetchData}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default PentestPage;
